<template>
  <main>
    <div>
      <h2 class="match-header recruiting-page-title">
        Recruiting
        <span v-if="statusFilter" class="recruiting-page-title__sub">— {{ statusLabelText }}</span>
      </h2>

      <div class="pd-both">
        <div class="md-layout sort-order-xsmall-100 recruiting-toolbar">
          <!-- Sort order (same pattern as Inbox.vue) -->
          <div class="md-layout-item">
            <label for="sortOrder">Sort Order</label>
            <div class="inline-sort w-25" @click="changeSortOrder()">
              <p v-text="`${sortOrder === -1 ? 'Newest First' : 'Oldest First'}`" class="sorter"></p>
              <font-awesome-icon class="small-icon m-left-5" icon="caret-down" v-if="sortOrder === -1" />
              <font-awesome-icon class="small-icon m-left-5" icon="caret-up" v-if="sortOrder !== -1" />
            </div>
          </div>

          <!-- Search: title or status text (client-side) -->
          <div class="md-layout-item">
            <md-field md-clearable class="recruiting-search">
              <md-input
                v-model="searchTerm"
                placeholder="Search by title or status"
                @input="searchTerm = sanitizeInput($event)"
              ></md-input>
            </md-field>
          </div>

          <!-- Community selector -->
          <div class="md-layout-item">
            <md-field class="align-right create-community w-50">
              <md-select
                @md-closed="loadData()"
                name="community"
                placeholder="Community"
                v-model="selectedCommunity"
                id="community"
                aria-label="Select community to filter"
              >
                <md-option v-for="c in allCommunities" :key="c._id" :value="c._id">{{ c.name }}</md-option>
              </md-select>
            </md-field>
          </div>
        </div>
      </div>

      <div v-if="loading">
        <md-empty-state md-label="Loading..." md-description="Fetching opportunities…"></md-empty-state>
      </div>

      <div v-else-if="notAccessible">
        <md-empty-state
          md-label="Recruiting is not enabled for this community"
          md-description="Toggle Recruiting Access from General Setting → Recruiting Access.">
        </md-empty-state>
      </div>

      <div v-else-if="!filteredSortedOpportunities.length">
        <md-empty-state
          md-label="No opportunities found"
          :md-description="emptyDescription">
        </md-empty-state>
      </div>

      <div v-else class="content-panel">
        <div class="inbox">
          <div v-for="opp in filteredSortedOpportunities" :key="opp._id">
            <recruiting-row :opportunity="opp" :community-style="communityStyle"></recruiting-row>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios'
import RecruitingRow from './RecruitingRow'
import API from '../services/API'
import { sanitizeInput } from '../utils/sanitizer'
import {
  CORE_API_URL,
  coreApiPageToHermesList,
  statusLabel,
  STATUS_LABELS
} from '../services/opportunityAdapter'
import { isFavorite } from '../services/recruitingFavoritesStore'

export default {
  name: 'RecruitingList',
  components: { RecruitingRow },
  data() {
    return {
      api: new API(),
      opportunities: [],
      loading: true,
      notAccessible: false,
      selectedCommunity: localStorage.getItem('community_id') || '',
      allCommunities: [],
      sortOrder: -1,      // -1 = Newest First, 1 = Oldest First
      searchTerm: '',
      communityStyle: {
        backgroundColor: 'white',
        color: 'black'
      }
    }
  },
  computed: {
    statusFilter() {
      const t = this.$route.query.term
      return typeof t === 'string' ? t : ''
    },
    statusLabelText() {
      return statusLabel(this.statusFilter)
    },
    emptyDescription() {
      if (this.searchTerm) return `No matches for "${this.searchTerm}".`
      if (this.statusFilter) return `No ${this.statusLabelText.toLowerCase()} opportunities for this community yet.`
      return 'No opportunities for this community yet.'
    },
    filteredSortedOpportunities() {
      const term = (this.searchTerm || '').trim().toLowerCase()
      let list = this.opportunities.slice()
      if (term) {
        list = list.filter(o => {
          const title = (o.title || '').toLowerCase()
          const statusText = (STATUS_LABELS[o.status] || o.status || '').toLowerCase()
          return title.indexOf(term) !== -1 || statusText.indexOf(term) !== -1
        })
      }
      list.sort((a, b) => {
        // Favorites always float to the top, regardless of the sort order;
        // within each group the chosen date order applies.
        const fa = isFavorite(a._id)
        const fb = isFavorite(b._id)
        if (fa !== fb) return fa ? -1 : 1
        const da = new Date(a.submittedDate || 0).getTime()
        const db = new Date(b.submittedDate || 0).getTime()
        return this.sortOrder === -1 ? db - da : da - db
      })
      return list
    }
  },
  watch: {
    $route: 'loadData'
  },
  async created() {
    await this.fetchCommunities()
    this.loadData()
  },
  methods: {
    sanitizeInput,
    changeSortOrder() {
      this.sortOrder = this.sortOrder === -1 ? 1 : -1
    },
    async fetchCommunities() {
      try {
        const res = await this.api.get('tenants')
        if (res && res.data && res.data.data) {
          this.allCommunities = res.data.data
            .map(t => ({ _id: t.communityId, name: t.communityName }))
            .sort((a, b) => a.name.localeCompare(b.name))
        }
      } catch (e) {
        this.allCommunities = []
      }
    },
    async fetchCommunityStyling(communityId) {
      // Same endpoint AskRow uses to get customSettings.primaryColor.
      this.communityStyle = { backgroundColor: 'white', color: 'black' }
      try {
        const res = await this.api.get(`communities/${communityId}/community-request`)
        const cs = res && res.data && res.data.customSettings
        if (cs && cs.primaryColor) {
          this.communityStyle = {
            backgroundColor: cs.primaryColor,
            color: cs.secondaryColor || '#fff'
          }
        }
      } catch (e) {
        // fall back to defaults
      }
    },
    async loadData() {
      this.loading = true
      this.notAccessible = false
      this.opportunities = []

      const community = this.selectedCommunity
      if (!community) {
        this.loading = false
        return
      }

      // Gate: recruiting.enabled must be true for this community, otherwise show "not accessible" message.
      try {
        const token = localStorage.getItem('auth0_token')
        const { data: recruiting } = await axios.get(
          `${CORE_API_URL}/api/recruiting/${community}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        if (!recruiting || recruiting.enabled !== true) {
          this.notAccessible = true
          this.loading = false
          return
        }
      } catch (e) {
        this.notAccessible = true
        this.loading = false
        return
      }

      // Community styling (background color per community)
      this.fetchCommunityStyling(community)

      // core-api /api/opportunities expects the community NAME (not _id),
      // matching how the dashboard talks to the same endpoint.
      const commObj = this.allCommunities.find(c => c._id === community)
      const communityName = commObj ? commObj.name : (localStorage.getItem('community_name') || '')
      // Fetch the 500 most recent opportunities. Server-side sort is
      // essential: without it Mongo returns insert-order, so newly created
      // opps (past the 500 cap) never reach the client.
      try {
        const params = {
          limit: 500,
          offset: 0,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        }
        if (communityName) params.community = communityName
        if (this.statusFilter) params.status = this.statusFilter
        const authToken = localStorage.getItem('auth0_token')
        const { data } = await axios.get(`${CORE_API_URL}/api/opportunities`, {
          params,
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        })
        this.opportunities = coreApiPageToHermesList(data)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[recruiting/list] failed:', e)
        this.opportunities = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.recruiting-page-title {
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: 700;
}
.recruiting-page-title__sub {
  font-weight: 400;
  color: #6c757d;
  margin-left: 6px;
  font-size: 0.7em;
}
.recruiting-toolbar {
  padding-top: 8px;
  padding-bottom: 12px;
}
.recruiting-search { margin-bottom: 0; }
</style>



// WEBPACK FOOTER //
// src/components/RecruitingList.vue